import { messageTemplateVariables } from 'ai-constants'
import React from 'react'
import { MdAddCircleOutline } from 'react-icons/md'
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Separator,
  Textarea,
} from 'ui'
import { useMessageTemplateForm } from '../hooks/useMessageTemplateForm'
import {
  DeliveryChannel,
  IMessageTemplate,
} from '../providers/MessageTemplate.provider'
import { IMessageTemplateSchema } from '../schemas/message-template.schema'
import { MessageTemplatePreview } from './MessageTemplatePreviews'
import { TranslatorOnlyString } from './Translator'

const AddVariable = (props: { onVariableSelected: (path: string) => void }) => {
  return (
    <div>
      <Popover modal>
        <PopoverTrigger asChild>
          <Button
            variant='ghost'
            className='text-gray-600 font-normal'
            size='sm'
          >
            <MdAddCircleOutline className='mr-2' />
            Añadir variable
          </Button>
        </PopoverTrigger>
        <PopoverContent>
          <ScrollArea
            orientation='vertical'
            className='max-h-60 h-full flex flex-col'
          >
            <div className='flex flex-col'>
              {messageTemplateVariables.map((ele, index) => {
                return (
                  <div key={index}>
                    <strong>
                      {TranslatorOnlyString({
                        children: ele.group,
                      })}
                    </strong>
                    {ele.paths.map((path) => {
                      return (
                        <div>
                          {(() => {
                            if (path.path) {
                              return (
                                <p
                                  className='transition-all cursor-pointer p-2 hover:bg-gray-100 ml-2'
                                  onClick={() =>
                                    props.onVariableSelected(
                                      `${ele.group}.${path.path}`,
                                    )
                                  }
                                >
                                  {TranslatorOnlyString({
                                    children: path.path,
                                  })}
                                </p>
                              )
                            }

                            return (
                              <div>
                                <Separator className='my-2' />
                                <strong>
                                  {TranslatorOnlyString({
                                    children: path.group,
                                  })}
                                </strong>
                                {(path.paths as { path: string }[])?.map(
                                  (nestedPath, index: number) => {
                                    return (
                                      <p
                                        key={index}
                                        className='transition-all ml-2 cursor-pointer p-2 hover:bg-gray-100'
                                        onClick={() =>
                                          props.onVariableSelected(
                                            `${ele.group}.${path.group}.${nestedPath.path}`,
                                          )
                                        }
                                      >
                                        {TranslatorOnlyString({
                                          children: nestedPath.path,
                                        })}
                                      </p>
                                    )
                                  },
                                )}
                              </div>
                            )
                          })()}
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  )
}

const FormFieldFooterMessage = ({
  children,
}: {
  children: React.ReactNode
}) => {
  return (
    <FormDescription className='m-0 flex-shrink-0'>{children}</FormDescription>
  )
}

const FormFieldFooter = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className='h-6 mt-2 flex flex-row justify-between items-center'>
      {children}
    </div>
  )
}

export const MessageTemplateEditorV2 = (props: {
  gatewayStatus?: IMessageTemplate['gatewayStatus']
  children?: React.ReactNode
  channel?: DeliveryChannel
  formId: string
  onSubmit: (data: IMessageTemplateSchema) => void
  defaultValues?: IMessageTemplateSchema
}) => {
  const {
    bodyRest,
    buttonLabelRest,
    customButtonLabelRef,
    deadlineDays,
    fields,
    footerRest,
    handleSubmit,
    logo,
    onAddVariable,
    subjectRest,
    errors,
    bodyRef,
    buttonLabelRef,
    footerRef,
    subjectRef,
    register,
    customSubjectRef,
    customBodyRef,
    customFooterRef,
    form,
  } = useMessageTemplateForm({
    channel: props.channel,
    defaultValues: props.defaultValues,
  })

  return (
    <div className='space-x-10 flex flex-row'>
      <div className='w-1/2'>
        <Form {...form}>
          <form
            onSubmit={handleSubmit(props.onSubmit)}
            id={props.formId}
            className='space-y-5 flex flex-col'
          >
            <FormField
              control={form.control}
              name={'subject'}
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>Encabezado</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        {...subjectRest}
                        className='bg-white'
                        ref={(e) => {
                          subjectRef(e)

                          customSubjectRef!.current! = e!
                        }}
                      />
                    </FormControl>
                    <FormFieldFooter>
                      <FormFieldFooterMessage>
                        Máximo 60 caracteres
                      </FormFieldFooterMessage>
                      <AddVariable
                        onVariableSelected={(path) =>
                          onAddVariable('subject', path)
                        }
                      />
                    </FormFieldFooter>
                  </FormItem>
                )
              }}
            />

            <FormField
              control={form.control}
              name={'body'}
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>Mensaje</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        {...bodyRest}
                        className='bg-white'
                        ref={(e) => {
                          bodyRef(e)

                          customBodyRef.current! = e!
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormFieldFooter>
                      <FormFieldFooterMessage>
                        Máximo 1024 caracteres
                      </FormFieldFooterMessage>
                      <AddVariable
                        onVariableSelected={(path) =>
                          onAddVariable('body', path)
                        }
                      />
                    </FormFieldFooter>
                  </FormItem>
                )
              }}
            />

            <FormField
              control={form.control}
              name={'buttonLabel'}
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>Texto del botón</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        {...buttonLabelRest}
                        className='bg-white'
                        ref={(e) => {
                          buttonLabelRef(e)
                          customButtonLabelRef!.current! = e!
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormFieldFooter>
                      <FormFieldFooterMessage>
                        Máximo 60 caracteres
                      </FormFieldFooterMessage>
                    </FormFieldFooter>
                  </FormItem>
                )
              }}
            />

            <FormField
              control={form.control}
              name={'footer'}
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>Pie de pagina</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        {...footerRest}
                        className='bg-white'
                        ref={(e) => {
                          footerRef(e)

                          customFooterRef!.current! = e!
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormFieldFooter>
                      <FormFieldFooterMessage>
                        Máximo 60 caracteres
                      </FormFieldFooterMessage>
                    </FormFieldFooter>
                  </FormItem>
                )
              }}
            />

            {props.children}
          </form>
        </Form>
      </div>
      <div className='w-[60%]'>
        <MessageTemplatePreview fields={fields} channel={props.channel} />
      </div>
    </div>
  )
}
